import React from 'react';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { OPPORTUNITY_LABELS } from '../../lib/solutionStages';

// Modelling chain for Single Measure ROI — Learning Measure first
// (that's the page's entry point), then the value transfer path.
const CHAIN = [
  { key: 'learning', label: 'Learning Measure' },
  { key: 'capability', label: 'Capability' },
  { key: 'behavior', label: 'Behavior' },
  { key: 'outcomes', label: 'Business Outcome' },
  { key: 'financial', label: 'Financial Impact' },
];

const CONSTRAINT_SUGGESTIONS = {
  learning: 'Consider strengthening engagement and completion assumptions.',
  capability: 'Consider improving job alignment or time-to-apply assumptions.',
  behavior: 'Consider increasing manager support or adoption assumptions.',
  outcomes: 'Consider reviewing KPI linkage and execution risk assumptions.',
  financial: 'Consider reviewing value-per-employee and conversion assumptions.',
};

/**
 * ValueTransferContextStrip — small contextual strip near the top of
 * the Single Measure ROI workspace. Shows the chain being modelled,
 * what the user is currently modelling (measure → KPI), and a
 * non-intrusive "Current Constraint Detected" guidance line when
 * Mission Control has an active value constraint.
 */
export default function ValueTransferContextStrip({ measureName, kpiNames = [], leak, leakPct }) {
  const kpiDisplay = kpiNames.length === 0
    ? null
    : kpiNames.length <= 2
      ? kpiNames.join(' + ')
      : `${kpiNames[0]} + ${kpiNames.length - 1} more`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="value-transfer-context-strip">
      <div className="flex flex-wrap items-center gap-y-2">
        {CHAIN.map((stage, i) => (
          <React.Fragment key={stage.key}>
            <span
              className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${
                leak?.key === stage.key
                  ? 'bg-orange-100 text-orange-800 border-orange-300'
                  : 'bg-slate-50 text-slate-600 border-slate-200'
              }`}
            >
              {stage.label}{leak?.key === stage.key ? ` −${leakPct}%` : ''}
            </span>
            {i < CHAIN.length - 1 && (
              <ArrowRight className="h-3.5 w-3.5 text-slate-300 mx-1.5 flex-shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>
      {measureName && (
        <p className="text-xs text-slate-600 mt-2.5" data-testid="currently-modelling-line">
          You are currently modelling:{' '}
          <span className="font-bold text-slate-900">{measureName}</span>
          {kpiDisplay && (
            <>
              {' '}<ArrowRight className="h-3 w-3 inline text-slate-400" />{' '}
              <span className="font-bold text-slate-900">{kpiDisplay}</span>
            </>
          )}
        </p>
      )}
      {leak && (
        <p className="flex items-start gap-1.5 text-xs text-orange-700 mt-1.5" data-testid="constraint-guidance-line">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
          <span>
            <span className="font-bold">Current Constraint Detected:</span>{' '}
            {OPPORTUNITY_LABELS[leak.key] || leak.stage.label} is currently the largest value transfer constraint (−{leakPct}%).{' '}
            {CONSTRAINT_SUGGESTIONS[leak.key]}
          </span>
        </p>
      )}
    </div>
  );
}
